import { useMutation, useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import {
  buildPlayerMatchHistoryRows,
  buildPlayerNameMap,
  buildSeasonProfileTableRows,
  describeHandicapCalculation,
  selectUsedHandicapRoundIds,
} from './seasonViews.js';
import { useAppState } from './AppState.js';
import { EmptyState, ErrorPanel, LoadingPanel, PageIntro, PanelHeader } from './components.js';
import { mapError } from './errors.js';
import { useRequiredLeagueId, useResolvedActiveSeason, useSyncLeagueFromRoute } from './hooks.js';
import { formatDate, formatOneDecimal } from '../lib/formatting.js';

function InfoTip({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <details className="profile-info-tip">
      <summary aria-label={label} title={label}>ⓘ</summary>
      <span className="profile-info-copy">{children}</span>
    </details>
  );
}

export function ProfilePage() {
  const leagueId = useRequiredLeagueId();
  const { playerId: targetPlayerId } = useParams();
  const { client, player, refreshIdentity, setBanner } = useAppState();
  useSyncLeagueFromRoute(leagueId);
  const { season } = useResolvedActiveSeason(leagueId);
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');

  const meQuery = useQuery({
    queryKey: ['me'],
    enabled: Boolean(client),
    queryFn: async () => client!.getCurrentUser(),
  });

  const seasonPlayersQuery = useQuery({
    queryKey: ['seasonPlayers', leagueId, season?.id ?? null],
    enabled: Boolean(client && season),
    queryFn: async () => (await client!.getSeasonPlayers(leagueId, season!.id)).seasonPlayers,
  });
  const profileHistoryQuery = useQuery({
    queryKey: ['profileHistory', leagueId, season?.id ?? null, targetPlayerId ?? meQuery.data?.player?.id ?? null],
    enabled: Boolean(client && season && (targetPlayerId ?? meQuery.data?.player?.id)),
    queryFn: async () =>
      client!.getPlayerSeasonProfileHistory(leagueId, season!.id, targetPlayerId ?? meQuery.data!.player!.id),
  });
  const handicapQuery = useQuery({
    queryKey: ['leagueHandicap', leagueId, targetPlayerId ?? meQuery.data?.player?.id ?? null],
    enabled: Boolean(client && season && (targetPlayerId ?? meQuery.data?.player?.id)),
    queryFn: async () => client!.getPlayerLeagueHandicap(leagueId, targetPlayerId ?? meQuery.data!.player!.id),
  });
  const seasonMatchesQuery = useQuery({
    queryKey: ['seasonMatches', leagueId, season?.id ?? null],
    enabled: Boolean(client && season),
    queryFn: async () => (await client!.getSeasonMatches(leagueId, season!.id)).matches,
  });

  const linkMutation = useMutation({
    mutationFn: async () => {
      if (!client) {
        throw new Error('API client unavailable');
      }
      return client.linkPlayer({
        email: email.trim(),
        ...(name.trim() ? { name: name.trim() } : {}),
      });
    },
    onSuccess: async () => {
      setBanner('success', 'Profile linked.');
      await refreshIdentity(true);
    },
    onError: (error: unknown) => setBanner('error', mapError(error)),
  });

  useEffect(() => {
    setEmail(player?.email ?? '');
    setName(player?.name ?? '');
  }, [player]);

  if (meQuery.isLoading) {
    return <LoadingPanel title="Profile" message="Loading player profile." />;
  }

  if (meQuery.isError || !meQuery.data) {
    return <ErrorPanel title="Profile" message={mapError(meQuery.error)} />;
  }

  if (!meQuery.data.player && !targetPlayerId) {
    return (
      <>
        <PageIntro
          title="Profile"
        />
        <section className="panel panel-accent">
          <form
            className="stack"
            onSubmit={(event) => {
              event.preventDefault();
              void linkMutation.mutateAsync();
            }}
          >
            <div className="field">
              <label>Email</label>
              <input type="email" required value={email} onChange={(event) => setEmail(event.target.value)} />
            </div>
            <div className="field">
              <label>Name</label>
              <input value={name} onChange={(event) => setName(event.target.value)} />
            </div>
            <div className="inline-actions">
              <button type="submit" disabled={linkMutation.isPending}>
                Link Player Profile
              </button>
            </div>
          </form>
        </section>
      </>
    );
  }

  if (season && (seasonPlayersQuery.isLoading || profileHistoryQuery.isLoading || seasonMatchesQuery.isLoading || handicapQuery.isLoading)) {
    return <LoadingPanel title="Profile" message="Loading season profile history." />;
  }

  if (season && (seasonPlayersQuery.isError || profileHistoryQuery.isError || seasonMatchesQuery.isError || handicapQuery.isError)) {
    return (
      <ErrorPanel
        title="Profile"
        message={mapError(
          seasonPlayersQuery.error ?? profileHistoryQuery.error ?? seasonMatchesQuery.error ?? handicapQuery.error,
        )}
      />
    );
  }

  const viewedPlayerId = targetPlayerId || meQuery.data.player?.id || null;
  const viewedSeasonPlayer =
    (seasonPlayersQuery.data ?? []).find((seasonPlayer) => seasonPlayer.player_id === viewedPlayerId) ?? null;
  const profileHistory = profileHistoryQuery.data ?? null;
  const viewedPlayer = profileHistory?.player ?? viewedSeasonPlayer?.player ?? meQuery.data.player ?? null;
  const playerNameById = buildPlayerNameMap(seasonPlayersQuery.data ?? []);
  const todayIso = new Date().toISOString().slice(0, 10);
  const historyRows =
    profileHistory?.seasonPlayer
      ? buildSeasonProfileTableRows(
          Number(profileHistory.seasonPlayer.starting_handicap || 0),
          profileHistory.history || [],
        )
      : [];
  const qualifyingRounds = handicapQuery.data?.rounds ?? [];
  const usedRoundIds = selectUsedHandicapRoundIds(qualifyingRounds);
  const matchHistoryRows =
    viewedPlayerId && season
      ? buildPlayerMatchHistoryRows(viewedPlayerId, seasonMatchesQuery.data ?? [], playerNameById, todayIso)
      : [];

  return (
    <>
      <PageIntro
        title={viewedPlayer?.name || 'Player Profile'}
        actions={
          season ? (
            <Link className="nav-link" to={`/leagues/${leagueId}/matches`}>
              Match Days
            </Link>
          ) : null
        }
      />
      <section className="grid two">
        <article className="panel">
          <PanelHeader
            title="Handicap and Match History"
            actions={handicapQuery.data
              ? <span className="badge scheduled">HCP {formatOneDecimal(handicapQuery.data.currentHandicapIndex)}</span>
              : null}
          />
          {!season ? (
            <EmptyState
              title="No active season selected"
              message="Choose a season in the header to load season-specific handicap history."
            />
          ) : historyRows.length === 0 ? (
            <EmptyState
              title="No score history found"
              message="Scores and handicap progression will appear here once rounds exist for the selected season."
            />
          ) : (
            <div className="table-wrap">
              <p className="profile-highlight-key">Highlighted differentials are used in the current handicap.</p>
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Course</th>
                    <th>Gross</th>
                    <th>
                      Differential <InfoTip label="About differentials">
                        Differential = (adjusted gross score − course rating) × 113 ÷ slope rating.
                        Adjusted gross caps each hole at par + 2 + strokes received.
                      </InfoTip>
                    </th>
                    <th>
                      New Handicap <InfoTip label="How the league handicap is calculated">
                        {describeHandicapCalculation(qualifyingRounds.length)} Qualifying rounds include completed,
                        non-absent regular, playoff, and sub rounds across all seasons. The result is rounded to
                        one decimal and limited to −10 through 27.
                      </InfoTip>
                    </th>
                    <th>Absent</th>
                  </tr>
                </thead>
                <tbody>
                  {historyRows.map((row, index) => (
                    <tr key={`${row.kind}-${row.roundId ?? index}`}>
                      <td>
                        {row.kind === 'starting' ? (
                          'Starting Handicap'
                        ) : row.matchDayId ? (
                          <Link to={`/leagues/${leagueId}/matches#match-day-${row.matchDayId}`}>
                            {formatDate(row.date)}
                          </Link>
                        ) : (
                          formatDate(row.date)
                        )}
                      </td>
                      <td>{row.courseName ?? '--'}</td>
                      <td>{row.grossScore === null ? '--' : row.grossScore}</td>
                      <td>
                        {row.handicapDifferential === null
                          ? '--'
                          : <span className={row.roundId && usedRoundIds.has(row.roundId) ? 'profile-used-differential' : undefined}>
                              {formatOneDecimal(Number(row.handicapDifferential))}
                            </span>}
                      </td>
                      <td>{formatOneDecimal(Number(row.handicapIndex))}</td>
                      <td>{row.kind === 'starting' ? '--' : row.playerAbsent ? 'Yes' : 'No'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </article>
        <article className="panel">
          <PanelHeader title="Match History" />
          {!season ? (
            <EmptyState
              title="No active season selected"
              message="Choose a season in the header to load season-specific match history."
            />
          ) : matchHistoryRows.length === 0 ? (
            <EmptyState
              title="No matches found"
              message="Completed matches and future scheduled matches for this player will appear here."
            />
          ) : (
            <div className="table-wrap">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Opponent</th>
                    <th>Score</th>
                    <th>W/L</th>
                  </tr>
                </thead>
                <tbody>
                  {matchHistoryRows.map((row) => (
                    <tr key={row.matchId}>
                      <td>{formatDate(row.date)}</td>
                      <td>{row.opponentName}</td>
                      <td>{row.score}</td>
                      <td>{row.result}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </article>
      </section>
    </>
  );
}
